import { z } from 'zod'
import { getPaginatedSchema, parseRequestError } from '@/common/utilities.ts'
import axiosInstance from '@/libs/axios_config.ts'

const parseDate = (dateString: string | null) => {
  if (dateString) {
    const parsedDate = new Date(dateString)
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate
    }
  }
  return null
}

export const GetUserPrograms = async (
  userId?: number
): Promise<UserProgramList> => {
  try {
    const response = await axiosInstance.get(
      `/users/${userId}/enrolled_programs`
    )
    return ProgramListSchema.parse(response.data?.data)
  } catch (e) {
    throw new Error(parseRequestError(e).message)
  }
}

type UserListStatusText = 'Active' | 'Inactive' | 'Pending'
export const getUserListStatusText = (status: number): UserListStatusText => {
  switch (status) {
    case 1:
      return 'Active'
    case 2:
      return 'Inactive'
    case 3:
      return 'Pending'
    default:
      return 'Active'
  }
}

const PublicProgramsResponse = z.object({
  id: z.number(),
  name: z.string(),
  global_state_id: z.number(),
  program_start_date: z.string(),
  program_end_date: z.string(),
})

const ProgramDetailsSchema = z.object({
  id: z.number(),
  status: z.number(),
  name: z.string().nullable(),
  global_state_id: z.number().nullable(),
  enrollment_start_date: z.string().transform(parseDate),
  enrollment_end_date: z.string().transform(parseDate),
  program_start_date: z.string().transform(parseDate),
  program_end_date: z.string().transform(parseDate),
  manager_id: z.number().nullable(),
  program_image: z.string().nullable(),
  created_by: z.string().nullable(),
  created_datetime: z.string().transform(parseDate),
  state_name: z.string().nullable(),
})

export type PublicProgramsResponse = z.infer<typeof PublicProgramsResponse>

const ProgramListSchema = z.array(ProgramDetailsSchema) // Expect an array directly from the API
export type UserProgramList = z.infer<typeof ProgramListSchema>
const PaginatedPublicProgramsSchema = getPaginatedSchema(PublicProgramsResponse)

export type PaginatedPublicPrograms = z.infer<
  typeof PaginatedPublicProgramsSchema
>

export const getPublicPrograms = async (): Promise<PaginatedPublicPrograms> => {
  try {
    const response = await axiosInstance.get('/public/programs')
    return PaginatedPublicProgramsSchema.parse(response.data)
  } catch (e) {
    throw new Error(parseRequestError(e).message)
  }
}
